"use client";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { t } from "@/lib/localization";

type Shortcut = { keys: string[]; label: string };

const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPod|iPhone|iPad/.test(navigator.platform);

const mod = isMac ? "⌘" : "Ctrl";

function getEditing(): Shortcut[] {
    return [
        {
            keys: [mod, "Z"],
            label: t("video-sheet.captions-tab.editor.help.shortcut.undo"),
        },
        {
            keys: [mod, "Shift", "Z"],
            label: t("video-sheet.captions-tab.editor.help.shortcut.redo"),
        },
        {
            keys: ["Enter"],
            label: t("video-sheet.captions-tab.editor.help.shortcut.split"),
        },
        {
            keys: ["Backspace"],
            label: t("video-sheet.captions-tab.editor.help.shortcut.merge"),
        },
    ];
}

function getNavigation(): Shortcut[] {
    return [
        {
            keys: ["Tab"],
            label: t("video-sheet.captions-tab.editor.help.shortcut.jump-next"),
        },
        {
            keys: ["Shift", "Tab"],
            label: t("video-sheet.captions-tab.editor.help.shortcut.jump-prev"),
        },
    ];
}

function getTools(): Shortcut[] {
    return [
        {
            keys: [mod, "F"],
            label: t("video-sheet.captions-tab.editor.help.shortcut.open-find"),
        },
        {
            keys: [mod, "S"],
            label: t("video-sheet.captions-tab.editor.help.shortcut.save"),
        },
        {
            keys: [mod, "/"],
            label: t("video-sheet.captions-tab.editor.help.shortcut.show-help"),
        },
    ];
}

function Key({ children }: { children: React.ReactNode }) {
    return (
        <kbd className="inline-flex min-w-[1.75rem] items-center justify-center rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground shadow-sm">
            {children}
        </kbd>
    );
}

function ShortcutRow({ shortcut }: { shortcut: Shortcut }) {
    return (
        <div className="flex items-center justify-between gap-4 py-1.5">
            <span className="text-sm text-muted-foreground">
                {shortcut.label}
            </span>
            <div className="flex items-center gap-1">
                {shortcut.keys.map((k, i) => (
                    <span key={i} className="flex items-center gap-1">
                        {i > 0 && (
                            <span className="text-xs text-muted-foreground">
                                +
                            </span>
                        )}
                        <Key>{k}</Key>
                    </span>
                ))}
            </div>
        </div>
    );
}

function Section({ title, items }: { title: string; items: Shortcut[] }) {
    return (
        <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {title}
            </h3>
            <div className="divide-y divide-border rounded-md border border-border bg-background">
                {items.map((s, i) => (
                    <div key={i} className="px-3">
                        <ShortcutRow shortcut={s} />
                    </div>
                ))}
            </div>
        </div>
    );
}

function LegendText() {
    const redLabel = t("video-sheet.captions-tab.editor.help.legend.red");
    const amberLabel = t("video-sheet.captions-tab.editor.help.legend.amber");
    const template = t("video-sheet.captions-tab.editor.help.legend.text");
    const parts = template.split(/(\{red\}|\{amber\})/g);
    return (
        <p>
            {parts.map((part, i) => {
                if (part === "{red}") {
                    return (
                        <span key={i} className="font-semibold text-red-600">
                            {redLabel}
                        </span>
                    );
                }
                if (part === "{amber}") {
                    return (
                        <span key={i} className="font-semibold text-amber-600">
                            {amberLabel}
                        </span>
                    );
                }
                return <span key={i}>{part}</span>;
            })}
        </p>
    );
}

export default function HelpModal({
    open,
    onOpenChange,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {t("video-sheet.captions-tab.editor.help.title")}
                    </DialogTitle>
                    <DialogDescription>
                        {t("video-sheet.captions-tab.editor.help.description")}
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-2 flex flex-col gap-4">
                    <Section
                        title={t(
                            "video-sheet.captions-tab.editor.help.section.navigation"
                        )}
                        items={getNavigation()}
                    />
                    <Section
                        title={t(
                            "video-sheet.captions-tab.editor.help.section.editing"
                        )}
                        items={getEditing()}
                    />
                    <Section
                        title={t(
                            "video-sheet.captions-tab.editor.help.section.tools"
                        )}
                        items={getTools()}
                    />

                    <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                        <LegendText />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => onOpenChange(false)}
                    >
                        {t("video-sheet.captions-tab.editor.help.got-it")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
